import { importLocationsParallel } from "#src/api/ai.js";
import cloneDeep from "lodash/cloneDeep";

import { DimText, Dimension, Quantity } from "dimtext";
import { createGridLines } from "@local/lamina-core";
import { almostEqual } from "overline";
import locationImage from "#src/extensions/location-image.js";
import extension from "@local/extensions/utilities/extension.js";
import nameMinusExtension from "@local/extensions/utilities/name-minus-extension.js";
import pageImage from "#src/extensions/page-image.js";
import PostStream from "#src/extensions/post-stream.js";
import Notifier from "#src/extensions/notifier.js";
import {
  multiplier,
  definedMultipliers,
} from "@local/extensions/utilities/scales.js";

async function analyzeOpeningClips(
  grp,
  locs,
  model,
  reasoning_level,
  organization_id,
  fileData,
) {
  const unit = grp.data.settings?.display_unit ?? "inches";

  const locsById = locs.reduce((acc, loc) => {
    acc[loc.id] = loc;
    return acc;
  }, {});
  const pageObj = locs.reduce((acc, loc) => {
    if (!acc[loc.document_id]) {
      acc[loc.document_id] = {};
    }

    if (!acc[loc.document_id][loc.page_id]) {
      acc[loc.document_id][loc.page_id] = true;
    }

    return acc;
  }, {});

  const pages = [];
  Object.keys(pageObj).forEach((docId) => {
    Object.keys(pageObj[docId]).forEach((pageId) => {
      const page = grp.documents[docId]?.data.pages[pageId];
      const sheet = fileData[page.name]?.sheets[page.page];
      if (page && sheet) {
        const snum = parseInt(sheet.name);
        const sname = sheet.name && !isNaN(snum) ? sheet.name : null;
        const label = page.label || sname;

        if (!label || !page.scale) {
          pages.push({
            document_id: docId,
            page_id: pageId,
            label,
            scale: page.scale,
            name: page.name,
            page: page.page,
          });
        }
      }
    });
  });
  const pageImages = await Promise.all(pages.map((page) => pageImage(page)));

  const locImages = await Promise.all(
    locs.map((loc) => locationImage(loc, grp)),
  );

  const response = await importLocationsParallel({
    locations: locs.map((loc, i) => {
      return {
        page_id: loc.page_id,
        id: loc.id,
        record_id: loc.record_id,
        clip: loc.clip,
        image: locImages[i],
        scale: loc.clip?.drawing_scale,
      };
    }),
    pages: pages.map((page, i) => ({ ...page, image: pageImages[i] })),
    model,
    reasoning_level,
    organization_id,
  });

  const notifier = new Notifier();
  const stream = new PostStream(response);

  const dt = new DimText({ defaultUnit: unit });
  let badResults = 0;
  let goodResults = 0;

  const pageData = {};

  let pageResultsReceived = 0;
  stream.on("page_result", (result) => {
    pageResultsReceived += 1;
    if (result.page) {
      if (!pageData[result.document_id]) {
        pageData[result.document_id] = {};
      }

      pageData[result.document_id][result.page_id] = result.page;
    }

    // All page results have been received, update the document
    // with the new page labels/scales
    if (pageResultsReceived === pages.length) {
      Object.keys(pageData).forEach((docId) => {
        const data = cloneDeep(grp.documents[docId].data);
        Object.keys(pageData[docId]).forEach((pageId) => {
          const page = data.pages[pageId];
          const res = pageData[docId][pageId];
          if (res.page && page) {
            const scale = `${res.page.scale.paper}:${res.page.scale.real}`;
            if (
              definedMultipliers[scale] != undefined ||
              res.page.page_name ||
              res.page.page_number
            ) {
              if (definedMultipliers[scale] != undefined && !page.scale) {
                page.scale = scale;
              }

              if (!page.label && !page.number && (res.page.page_name || res.page.page_number)) {
                // Keep the two halves separate: the sheet number identifies
                // the sheet in lists and chips, the title is too long for
                // them. `label` stays populated for existing consumers.
                if (res.page.page_number) page.number = res.page.page_number;
                if (res.page.page_name) page.title = res.page.page_name;

                if (res.page.page_name && res.page.page_number) {
                  page.label = `${res.page.page_number} - ${res.page.page_name}`;
                } else if (res.page.page_name) {
                  page.label = res.page.page_name;
                } else if (res.page.page_number) {
                  page.label = res.page.page_number;
                }
              }
            }
          }
        });

        notifier.dispatch("update", [
          {
            type: "document",
            id: docId,
            diff: { data },
          },
        ]);
      });
    }
  });

  let totalResults = 0;
  stream.on("location_result", (result) => {
    totalResults += 1;
    const updates = [];
    const loc = locsById[result.id];

    if (result.error || !result.location) {
      badResults += 1;
      updates.push({
        type: "item",
        id: result.record_id,
        diff: {
          approval_status: "flagged",
          llm_request_log_id: result.llm_request_id,
        },
      });
      notifier.dispatch("update", updates);
    } else {
      goodResults += 1;

      const item = grp.items[result.record_id];
      const rloc = result.location;
      if (item) {
        const data = cloneDeep(item.data);
        const drawing = cloneDeep(item.drawing) || {};
        const width = dt.parse(rloc.width)?.value || null;
        const height = dt.parse(rloc.height)?.value || null;
        const merges = rloc.merged_cells || [];
        let approval_status = "none";

        let rheights = rloc.row_heights || [];
        if (rheights.length === 0 && rloc.y_subdivisions === 1) {
          rheights = [rloc.height];
        }

        let cwidths = rloc.column_widths || [];
        if (cwidths.length === 0 && rloc.x_subdivisions === 1) {
          cwidths = [rloc.width];
        }

        if (rloc.x_subdivisions > 0 && rloc.y_subdivisions > 0) {
          let w = width != null ? width.toNumber("inches") : null;

          if (!w && item.width) {
            w = item.cache.width_in;
          }

          let h = height != null ? height.toNumber("inches") : null;

          if (!h && item.height) {
            h = item.cache.height_in;
          }
          if (w && h) {
            const gl = createGridLines(w, h, cwidths.length, rheights.length);

            if (cwidths.length === rloc.x_subdivisions) {
              const twidth = cwidths.reduce((total, cw) => {
                const dim = dt.parse(cw);
                if (dim.ok) {
                  const v = dim.value.toNumber("inches");
                  return total + v;
                }
                return total;
              }, 0);

              if (almostEqual(twidth, w)) {
                cwidths.forEach((cw, i) => {
                  const dim = dt.parse(cw);
                  if (dim.ok) {
                    gl.vertical[i].value = dim.value;
                  }
                });
              } else {
                const diff = w - twidth;
                // Distribute the difference among the divisions
                const spacer = diff / (rloc.x_subdivisions + 1);
                const halfspacer = spacer / 2;
                cwidths.forEach((cw, i) => {
                  const d =
                    i === 0 || i === cwidths.length - 1
                      ? halfspacer * 3
                      : halfspacer * 2;
                  const dim = dt.parse(cw);
                  if (dim.ok) {
                    const v = dim.value.toNumber("inches");
                    gl.vertical[i].value = new Dimension(
                      new Quantity(v + d, "inches"),
                    );
                  }
                });
              }
            }

            if (rheights.length === rloc.y_subdivisions) {
              const theight = rheights.reduce((total, rh) => {
                const dim = dt.parse(rh);
                if (dim.ok) {
                  const v = dim.value.toNumber("inches");
                  return total + v;
                }
                return total;
              }, 0);

              if (almostEqual(theight, h)) {
                rheights.forEach((rh, i) => {
                  const hIndex = rheights.length - 1 - i;
                  const dim = dt.parse(rh);
                  if (dim.ok) {
                    gl.horizontal[hIndex].value = dim.value;
                  }
                });
              } else {
                const diff = h - theight;
                // Distribute the difference among the divisions
                const spacer = diff / (rloc.y_subdivisions + 1);
                const halfspacer = spacer / 2;
                rheights.forEach((rh, i) => {
                  const hIndex = rheights.length - 1 - i;
                  const d =
                    i === 0 || i === rheights.length - 1
                      ? halfspacer * 3
                      : halfspacer * 2;
                  const dim = dt.parse(rh);
                  if (dim.ok) {
                    const v = dim.value.toNumber("inches");
                    gl.horizontal[hIndex].value = new Dimension(
                      new Quantity(v + d, "inches"),
                    );
                  }
                });
              }
            }

            merges.forEach((merge) => {
              if (!gl.merges) {
                gl.merges = [];
              }

              if (merge.rowspan > 1 || merge.colspan > 1) {
                gl.merges.push(merge);
              }
            });

            drawing.attachment_id = loc.id;
            data.reference_planes = gl;

            const doc = grp.documents[loc.document_id];
            const page = doc.data.pages[loc.page_id];
            const ext = extension(page.name);

            drawing.clip = loc.clip;
            drawing.properties = {
              width: loc.clip.width,
              height: loc.clip.height,
            };
            drawing.extension = ext;

            if (ext === "pdf") {
              drawing.id = page.name.split("/")[0];
              drawing.name = page.label;
              drawing.type = "iiif";
              drawing.path = `${nameMinusExtension(page.name)}.${page.page}.tiff`;
            } else {
              // TODO: impelement support for non-pdf drawings
            }

            if (drawing.clip && !drawing.clip.drawing_scale) {
              const pd = pageData[loc.document_id]?.[loc.page_id];
              if (pd?.page?.scale) {
                const scale = `${pd.page.scale.paper}:${pd.page.scale.real}`;
                if (definedMultipliers[scale] != undefined) {
                  drawing.clip.drawing_scale = scale;
                }
              }
            }

            if (rloc.registration_point && drawing.clip?.drawing_scale) {
              const m = multiplier(drawing.clip.drawing_scale);
              const ch = (h * 300) / m;

              const ypx = drawing.clip.height - ch - rloc.registration_point.y;
              const xpx = rloc.registration_point.x;

              const y = (ypx * m) / 300;
              const x = (xpx * m) / 300;

              drawing.clip.drawing_offset = {
                x: -x,
                y: -y,
              };

              updates.push({
                type: "location",
                id: loc.id,
                diff: {
                  clip: drawing.clip,
                },
              });
            }
          } else {
            approval_status = "flagged";
          }
        }

        updates.push({
          type: "item",
          id: result.record_id,
          diff: {
            mark: rloc.mark,
            ...(width ? { width } : {}),
            ...(height ? { height } : {}),
            x_subdivisions: rloc.x_subdivisions,
            y_subdivisions: rloc.y_subdivisions,
            data,
            show_drawing_overlay: !!drawing.clip?.drawing_scale,
            drawing,
            approval_status,
            llm_request_log_id: result.llm_request_id,
          },
        });

        notifier.dispatch("update", updates);
      }
    }
    notifier.dispatch("location-result", { id: result.id });
  });

  stream.on("error", (error) => {
    notifier.dispatch("error", error);
  });

  stream.on("done", () => {
    notifier.dispatch("done", {
      successCount: goodResults,
      failCount: badResults,
      totalCount: totalResults,
    });
  });

  return notifier;
}

export default analyzeOpeningClips;
